import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'
import { InputSwitch } from 'primereact/inputswitch'

import ItemsList from '@aytm/app/stats/page/question-views/components/items-list/ItemsList'

import { batchElementId } from '@aytm/app/stats/page/components/lib/utils'
import { calcUncollapsedKeys } from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'

import { calcBatchAnswer, calcCurrentPackageCoeffs } from '@aytm/app/stats/page/question-views/cjc/lib/calc'
import { ItemHeader, ItemBody } from '@aytm/app/stats/page/question-views/cjc/components/items/Item'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import style from '@aytm/app/stats/page/question-views/cjc/components/items/styles/Items.module.scss'

const identity = text => text

// Legacy sortTable: position sort is plain; importance sort tie-breaks on position.
const sortBatches = (rows, sortOrder) => {
  const val = r => r.bAnswer.importance
  const pos = r => r.batch.position
  return rows.slice().sort((a, b) => {
    switch (sortOrder) {
      case 'vasc':
        return val(a) + pos(a) / 100000 - (val(b) + pos(b) / 100000)
      case 'vdesc':
        return val(b) + pos(b) / 100000 - (val(a) + pos(a) / 100000)
      case 'idesc':
        return pos(b) - pos(a)
      case 'iasc':
      default:
        return pos(a) - pos(b)
    }
  })
}

const SortMarker = ({ active, dir }) => (
  <span className={classNames(style.sort, style[dir], { [style.active]: active })} />
)

SortMarker.propTypes = { active: PropTypes.bool, dir: PropTypes.oneOf(['asc', 'desc']) }

// Conjoint subquestion list: header (attribute sort + mode InputSwitch + expand-all +
// importance sort) over the reusable ItemsList. State (mode / sortOrder / uncollapsed /
// currentPackage) is owned upstream (the Backbone model, via block.coffee); this component
// is controlled and reports changes back through the callbacks.
const Items = ({
  id,
  items,
  answer,
  mode,
  persona,
  currentPackage,
  sortOrder,
  uncollapsed,
  isSummaryView,
  hideTotal,
  processText,
  onModeChange,
  onSortChange,
  onToggleCollapse,
  onToggleAll,
  onPackageChange
}) => {
  const openKeys = useMemo(() => calcUncollapsedKeys(uncollapsed), [uncollapsed])

  // Per-batch answer (coefficients / impact / importance), then ordered by the active sort.
  const rows = useMemo(() => {
    const currentPackageCoeffs = calcCurrentPackageCoeffs(mode, answer, persona, currentPackage)
    const built = (items || [])
      .filter(batch => batch && !batch.na)
      .map(batch => ({
        batch,
        bAnswer: calcBatchAnswer({ answer, batch, mode, persona, currentPackage, currentPackageCoeffs, isSummaryView })
      }))
    return sortBatches(built, sortOrder)
  }, [items, answer, mode, persona, currentPackage, sortOrder, isSummaryView])

  const total = answer?.clusters?.[persona - 1]?.total ?? answer?.total

  const byPosition = 'iasc' === sortOrder || 'idesc' === sortOrder
  const byImportance = 'vasc' === sortOrder || 'vdesc' === sortOrder

  const rowsByPosition = useMemo(
    () =>
      rows.reduce((acc, cur) => {
        acc[cur.batch.position] = cur
        return acc
      }, {}),
    [rows]
  )

  return (
    <>
      <div className={style.itemAbout}>
        <span className={style.attrTitle} {...pressable(() => onSortChange('iasc' === sortOrder ? 'idesc' : 'iasc'))}>
          Attributes
          <SortMarker dir={'iasc' === sortOrder ? 'asc' : 'desc'} active={byPosition} />
        </span>

        <span className={style.modeToggle}>
          <span className={style.modeLabel} onClick={() => onModeChange('exp')}>
            Preference likelihood
          </span>
          <InputSwitch
            aria-labelledby={`${id}-mode-sum-label`}
            checked={'sum' === mode}
            onChange={e => onModeChange(e.value ? 'sum' : 'exp')}
          />
          <span id={`${id}-mode-sum-label`} className={style.modeLabel} onClick={() => onModeChange('sum')}>
            Utility scores
          </span>
        </span>

        <span className={style.itemsExpand} {...pressable(() => onToggleAll(0 < openKeys.length))}>
          {0 < openKeys.length ? 'Collapse all' : 'Expand all'}
        </span>

        <span className={style.responses}>
          <span
            className={style.responsesTitle}
            {...pressable(() => onSortChange('vasc' === sortOrder ? 'vdesc' : 'vasc'))}
          >
            Importance {total != null && !hideTotal ? <em>({total} resp.)</em> : null}
            <SortMarker dir={'vasc' === sortOrder ? 'asc' : 'desc'} active={byImportance} />
          </span>
        </span>
      </div>

      <ItemsList
        items={rows.map(r => r.batch)}
        itemId={batch => batchElementId(id, batch.position)}
        itemKey={batch => batch.position}
        openKeys={openKeys}
        renderHeader={(batch, { collapsed }) => (
          <ItemHeader
            item={batch}
            bAnswer={rowsByPosition[batch.position]?.bAnswer}
            collapsed={collapsed}
            processText={processText}
          />
        )}
        renderBody={batch => (
          <ItemBody
            id={`${id}-${batch.position}`}
            item={batch}
            bAnswer={rowsByPosition[batch.position]?.bAnswer}
            processText={processText}
            onPackageChange={onPackageChange}
          />
        )}
        onToggle={(key, willCollapse) => onToggleCollapse(key, willCollapse)}
      />
    </>
  )
}

Items.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  items: PropTypes.array,
  answer: answerPropType,
  mode: PropTypes.oneOf(['exp', 'sum']),
  persona: PropTypes.number,
  currentPackage: PropTypes.string,
  sortOrder: PropTypes.oneOf(['iasc', 'idesc', 'vasc', 'vdesc']),
  uncollapsed: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isSummaryView: PropTypes.bool,
  hideTotal: PropTypes.bool,
  processText: PropTypes.func,
  onModeChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  onPackageChange: PropTypes.func.isRequired
}

Items.defaultProps = {
  mode: 'exp',
  persona: 0,
  sortOrder: 'iasc',
  processText: identity
}

Items.displayName = 'ConjointItems'

export default Items
